type PayperiodFilterLike = {
    start_datetime?: string;
    end_datetime?: string;
    start_date_ui?: string;
    end_date_ui?: string;
};

type PayperiodOption = {
    value: string;
    label: string;
};

type PayPeriodNavigationState = {
    payPeriod?: string;
    payPeriodStart?: string;
    payPeriodEnd?: string;
    targetDate?: string;
};

type ResolveAutoSelectedPayperiodArgs = {
    payperiodOptions: PayperiodOption[];
    locationState?: PayPeriodNavigationState | null;
    storageKey: string;
    storageDateKey?: string;
    today?: string;
};

const getDatePart = (value?: string | null): string => value?.split(" ")[0]?.trim() || "";

const getNavigationPayperiodValue = (locationState?: PayPeriodNavigationState | null): string | null => {
    if (!locationState) {
        return null;
    }

    if (locationState.payPeriod?.trim()) {
        return locationState.payPeriod.trim();
    }

    if (locationState.payPeriodStart && locationState.payPeriodEnd) {
        return `${locationState.payPeriodStart} - ${locationState.payPeriodEnd}`;
    }

    return null;
};

export const buildPayperiodOptions = (payperiodFilters: PayperiodFilterLike[] = []): PayperiodOption[] =>
    payperiodFilters.map((payperiod) => ({
        value: `${payperiod.start_datetime} - ${payperiod.end_datetime}`,
        label: `${payperiod.start_date_ui} - ${payperiod.end_date_ui}`,
    }));

export const findPayperiodOptionForDate = (
    payperiodOptions: PayperiodOption[],
    targetDate?: string | null,
): PayperiodOption | undefined => {
    const normalizedTargetDate = getDatePart(targetDate);

    if (!normalizedTargetDate) {
        return undefined;
    }

    return payperiodOptions.find((option) => {
        const [startDate, endDate] = option.value.split(" - ");
        const normalizedStartDate = getDatePart(startDate);
        const normalizedEndDate = getDatePart(endDate);

        if (!normalizedStartDate || !normalizedEndDate) {
            return false;
        }

        return normalizedTargetDate >= normalizedStartDate && normalizedTargetDate <= normalizedEndDate;
    });
};

export const resolveAutoSelectedPayperiod = ({
    payperiodOptions,
    locationState,
    storageKey,
    storageDateKey,
    today = new Date().toISOString().split("T")[0],
}: ResolveAutoSelectedPayperiodArgs): string | null => {
    if (!payperiodOptions.length) {
        return null;
    }

    const navigationPayperiod = getNavigationPayperiodValue(locationState);
    if (navigationPayperiod) {
        const matchedNavigationOption = payperiodOptions.find((option) => option.value === navigationPayperiod);
        if (matchedNavigationOption) {
            return matchedNavigationOption.value;
        }
    }

    const matchedTargetDateOption = findPayperiodOptionForDate(payperiodOptions, locationState?.targetDate);
    if (matchedTargetDateOption) {
        return matchedTargetDateOption.value;
    }

    let canUseStoredPayperiod = true;
    if (storageDateKey) {
        const storedDate = localStorage.getItem(storageDateKey);
        if (storedDate !== today) {
            canUseStoredPayperiod = false;
            localStorage.setItem(storageDateKey, today);
        }
    }

    if (canUseStoredPayperiod) {
        const storedPayperiod = localStorage.getItem(storageKey);
        const matchedStoredOption = payperiodOptions.find((option) => option.value === storedPayperiod);
        if (matchedStoredOption) {
            return matchedStoredOption.value;
        }
    }

    return payperiodOptions[0].value;
};
